import loadable from '@loadable/component';
import installSubblocksStateSync from './config/syncSubblocksState';
import './theme/form.css';
import type { ConfigType } from '@plone/registry';
import installSettings from './config/settings';
import installLino from './components/Lino/config';
import ListingCardTemplate from './components/Listing/CardTemplate';
import {
  ButtonEdit,
  ButtonView,
  ButtonSchema,
} from './components/Button/Button';
import linkSVG from '@plone/volto/icons/link.svg';
import { LinksEdit, LinksView, LinksSchema } from './components/Links/Links';
import listSVG from '@plone/volto/icons/list-bullet.svg';
import { QuoteEdit, QuoteView, QuoteSchema } from './components/Quote/Quote';
import quoteSVG from '@plone/volto/icons/quote.svg';
import { LogosEdit, LogosView, LogosSchema } from './components/Logos/Logos';
import imagesSVG from '@plone/volto/icons/images.svg';
import { HeroEdit, HeroView, HeroSchema } from './components/Hero/Hero';
import heroSVG from '@plone/volto/icons/hero.svg';
import {
  SliderEdit,
  SliderView,
  SliderSchema,
} from './components/Slider/Slider';
import sliderSVG from '@plone/volto/icons/slider.svg';
import '@fontsource-variable/roboto';
import '@fontsource-variable/roboto/wght-italic.css';
import '@fontsource-variable/exo-2';
import './theme/tokens.css';
import './theme/layout.css';
import './theme/typography.css';
import './theme/spacing.css';
import './theme/sections.css';
import './theme/columns.css';
import './theme/accordion.css';
import './theme/image.css';
import './theme/video.css';
import './theme/teaser.css';
import './theme/button.css';
import './theme/links.css';
import './theme/quote.css';
import './theme/logos.css';
import './theme/hero.css';
import './theme/slider.css';
import './theme/statistic.css';
import './theme/listing.css';
import './theme/chrome.css';
import './theme/conference.css';

function applyConfig(config: ConfigType) {
  installSettings(config);
  installLino(config);
  installSubblocksStateSync();
  // The form add-on still uses these Volto 18 lazy-library names.
  const formLoadables = config.settings.loadables as Record<string, unknown>;
  formLoadables.reactDnd ??= loadable.lib(() => import('react-dnd'), {
    ssr: false,
  });
  formLoadables.reactDndHtml5Backend ??= loadable.lib(
    () => import('react-dnd-html5-backend'),
    { ssr: false },
  );
  config.blocks.blocksConfig.sliderBlock = {
    id: 'sliderBlock',
    title: 'Slider',
    icon: sliderSVG,
    group: 'common',
    edit: SliderEdit,
    view: SliderView,
    blockSchema: SliderSchema,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };
  config.blocks.blocksConfig.heroBlock = {
    id: 'heroBlock',
    title: 'Hero',
    icon: heroSVG,
    group: 'common',
    edit: HeroEdit,
    view: HeroView,
    blockSchema: HeroSchema,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };
  config.blocks.blocksConfig.logosBlock = {
    id: 'logosBlock',
    title: 'Logos',
    icon: imagesSVG,
    group: 'common',
    edit: LogosEdit,
    view: LogosView,
    blockSchema: LogosSchema,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };
  config.blocks.blocksConfig.quoteBlock = {
    id: 'quoteBlock',
    title: 'Quote',
    icon: quoteSVG,
    group: 'common',
    edit: QuoteEdit,
    view: QuoteView,
    blockSchema: QuoteSchema,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };
  config.blocks.blocksConfig.linksBlock = {
    id: 'linksBlock',
    title: 'Links',
    icon: listSVG,
    group: 'common',
    edit: LinksEdit,
    view: LinksView,
    blockSchema: LinksSchema,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };
  config.blocks.blocksConfig.buttonBlock = {
    id: 'buttonBlock',
    title: 'Button',
    icon: linkSVG,
    group: 'common',
    edit: ButtonEdit,
    view: ButtonView,
    blockSchema: ButtonSchema,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };
  config.blocks.blocksConfig.listing.variations.push(
    { id: 'news', title: 'News', template: ListingCardTemplate },
    { id: 'events', title: 'Events', template: ListingCardTemplate },
    { id: 'contacts', title: 'Contacts', template: ListingCardTemplate },
  );
  config.blocks.blocksConfig.group.title = 'Section';
  const accordion = config.blocks.blocksConfig
    .accordion as typeof config.blocks.blocksConfig.accordion & {
    titleIcons: { size: string };
  };
  accordion.titleIcons.size = '29px';
  const teaserVariations = config.blocks.blocksConfig.teaser.variations;
  teaserVariations[0].title = 'Plain';
  teaserVariations.push({
    id: 'cover',
    title: 'Cover',
    template: teaserVariations[0].template,
  });
  config.blocks.blocksConfig.gridBlock.restricted = true;
  config.blocks.blocksConfig.html.restricted = true;
  config.settings.styleClassNameExtenders.push(({ data, classNames }) =>
    data['@type'] === 'teaser'
      ? [
          ...classNames.filter((name) => !name.startsWith('has--align--')),
          'has--align--center',
        ]
      : classNames,
  );

  return config;
}

export default applyConfig;
